import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Theme } from '@app/theme';
import { BillingInformation } from 'queries';

import { Input, CountryKeySelect } from '@app/form';
import { VatNumberField } from 'components';
import { shouldApplyVat } from 'lib/billing';
import {
  franceCountryCodes,
  vatNumberRequiredCountries,
} from 'config/euCountries';

const useStyles = createUseStyles((theme: Theme) => ({
  sameLine: {
    marginTop: theme.marginBase * 3,
    marginRight: -theme.marginBase * 2,
    display: 'flex',
    '& > *': {
      marginRight: theme.marginBase * 2,
    },
  },
}));

interface Props {
  initialValues: BillingInformation;
  onSuccess?: () => any;
  onShouldApplyVatChange?: (val: boolean) => any;
  name?: string;
  addressOnly?: boolean;
}

const useCallShouldApplyVatCallback = (
  initialValues: BillingInformation,
  onShouldApplyVatChange?: (val: boolean) => any,
) => {
  const [countryKey, setCountryKey] = useState(
    initialValues.billingAddress?.country,
  );
  const applyVATRef = useRef<boolean | null>(null);

  const onCountryChange = useCallback(
    (option: { value: string }) => {
      setCountryKey(option.value);
      return true;
    },
    [setCountryKey],
  );

  useEffect(() => {
    if (!onShouldApplyVatChange || !countryKey) {
      return;
    }
    const shouldApplyTaxes = shouldApplyVat(countryKey);
    if (shouldApplyTaxes !== applyVATRef.current) {
      onShouldApplyVatChange(shouldApplyTaxes);
      applyVATRef.current = shouldApplyTaxes;
    }
  }, [countryKey, applyVATRef, onShouldApplyVatChange]);

  return { onCountryChange, countryKey };
};

const emptyValues = {
  billingOrganization: '',
  email: '',
  billingAddress: {
    line1: '',
    line2: '',
    postalCode: '',
    city: '',
    country: '',
  },
  vatNumber: null,
  locale: 'en',
};

const addressFieldsSchema = {
  email: Yup.string().email().required(),
  billingAddress: Yup.object()
    .shape({
      line1: Yup.string().required(),
      line2: Yup.string().nullable(),
      postalCode: Yup.string().required(),
      city: Yup.string().required(),
      country: Yup.string().required(),
    })
    .required(),
};

export const billingValidationSchema = Yup.object().shape({
  ...addressFieldsSchema,
  billingOrganization: Yup.string().required(),
  vatNumber: Yup.string()
    .nullable()
    .when('billingAddress', {
      is: (billingAddress?: { country?: string }) =>
        vatNumberRequiredCountries.includes(billingAddress?.country ?? '') ||
        franceCountryCodes.includes(billingAddress?.country || ''),
      then: (schema) => schema.required('form.errors.required'),
    }),
});
export const addressOnlyValidationSchema =
  Yup.object().shape(addressFieldsSchema);

export const useBillingInfoInitialValues = (
  billingInformation: BillingInformation,
) => {
  const { i18n } = useTranslation();
  return useMemo(() => {
    return {
      ...emptyValues,
      ...billingInformation,
      billingAddress: {
        ...emptyValues.billingAddress,
        ...billingInformation.billingAddress,
        locale: i18n.language.startsWith('fr') ? 'fr' : 'en',
      },
    };
  }, [billingInformation, i18n.language]);
};

export const BillingInformationFormFields = ({
  initialValues,
  onShouldApplyVatChange,
  name,
  addressOnly,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { onCountryChange } = useCallShouldApplyVatCallback(
    initialValues,
    onShouldApplyVatChange,
  );

  const getFieldName = (fieldName: string) => {
    if (!name) {
      return fieldName;
    }
    return `${name}.${fieldName}`;
  };

  return (
    <>
      {!addressOnly && (
        <Input
          name={getFieldName('billingOrganization')}
          label={t('billing.informations.display.name')}
          requiredStar
        />
      )}
      <div className={classes.sameLine}>
        <Input
          noMargin
          name={getFieldName('billingAddress.line1')}
          label={t('billing.informations.display.addressLine1')}
          requiredStar
        />
        <Input
          noMargin
          name={getFieldName('billingAddress.line2')}
          label={t('billing.informations.display.addressLine2')}
        />
      </div>
      <div className={classes.sameLine}>
        <Input
          noMargin
          name={getFieldName('billingAddress.postalCode')}
          label={t('billing.informations.display.addressPostalCode')}
          requiredStar
        />
        <Input
          noMargin
          name={getFieldName('billingAddress.city')}
          label={t('billing.informations.display.addressCity')}
          requiredStar
        />
        <CountryKeySelect
          noMargin
          name={getFieldName('billingAddress.country')}
          label={t('billing.informations.display.addressCountry')}
          requiredStar
          onChange={onCountryChange as any}
        />
      </div>
      <div className={classes.sameLine}>
        {!addressOnly && (
          <Field name={name}>
            {({ meta }: FieldProps) => (
              <VatNumberField
                noMargin
                name={getFieldName('vatNumber')}
                label={t('billing.informations.display.vatNumber')}
                currentCountry={meta.value.billingAddress.country}
                requiredStar={vatNumberRequiredCountries.includes(
                  meta.value.billingAddress.country,
                )}
              />
            )}
          </Field>
        )}
        <Input
          noMargin
          name={getFieldName('email')}
          label={t('billing.informations.display.email')}
          requiredStar
        />
      </div>
    </>
  );
};
